// Health Center API client (cloud-api /api/v1/health-center/*).
import { api } from './api';

export interface HealthSummary {
  // null when the tenant has no devices (nothing to score) — render a
  // "no devices" state instead of a misleading 0%/100%.
  systemScore: number | null;
  globalScore: number | null;
  totalDevices: number;
  online: number;
  offline: number;
  critical: number;
  warnings: number;
  lastScanTime: string;
  sites: Array<{ siteId: string; siteName: string; customerName?: string; region?: string; totalDevices: number; online: number; critical: number; warnings: number; score: number }>;
  customerScores: Array<{ id: string; name: string; score: number }>;
  regionalScores: Array<{ id: string; name: string; score: number }>;
  breakdown: Array<{ event: string; label: string; count: number; pointsLost: number }>;
  driverMix: Array<{ driverSlug: string; count: number }>;
  history7d: Array<{ capturedAt: string; systemScore: number; online: number; critical: number; warnings: number; recordingCameras: number }>;
  delta24h?: { systemScore: number; online: number; critical: number };
}

export interface PredictionAlert {
  type: string; deviceId: string; deviceName: string; detail: string; severity: string;
}

// A real, fault-derived incident (auto-opened by the health/video probes +
// device-event bridge). deviceName/deviceCoreType say WHICH device (NVR vs
// channel); faultCategory groups it (Connectivity/Video/Storage/Security/…).
export interface HealthIncident {
  id: string; incidentNumber: string; type: string; category: string;
  faultCategory: string; event: string | null;
  title: string; description: string; status: string; priority: string;
  siteId: string | null; relatedDevices: string[];
  deviceName: string | null; deviceCoreType: string | null;
  auto: boolean; reportedAt: string; resolvedAt: string | null;
  // Local-AI-reasoning insight (root-cause + recommendation) when the edge
  // reasoner enriched this incident. Null otherwise.
  aiInsight: AiInsight | null;
}

export interface AiInsight {
  summary: string; rootCause: string; recommendation: string;
  confidence: number; source: 'heuristic' | 'llm'; generatedAt: string;
}

export const healthApi = {
  summary: async () => (await api.get<{ data: HealthSummary }>('/api/v1/health-center/summary')).data.data,
  predictions: async () =>
    (await api.get<{ data: { alerts: PredictionAlert[] } }>('/api/v1/health-center/predictions')).data.data.alerts,
  incidents: async (params?: Record<string, string>) => {
    const e = Object.entries(params ?? {}).filter(([, v]) => v);
    const qs = e.length ? `?${new URLSearchParams(Object.fromEntries(e)).toString()}` : '';
    return (await api.get<{ data: HealthIncident[] }>(`/api/v1/incidents${qs}`)).data.data;
  },
};
